import { FeaturePageLayout } from "@/components/features/FeaturePageLayout";
import { FeatureHero } from "@/components/features/FeatureHero";
import { FeatureSection } from "@/components/features/FeatureSection";
import { FeatureCard } from "@/components/features/FeatureCard";
import { FeatureGrid } from "@/components/features/FeatureGrid";
import { HowItWorksSection } from "@/components/features/HowItWorksSection";
import { ScreenshotSection } from "@/components/features/ScreenshotSection";
import { RelatedFeaturesSection } from "@/components/features/RelatedFeaturesSection";
import { FeatureCTA } from "@/components/features/FeatureCTA";
import {
  Mail,
  Palette,
  Zap,
  Calendar,
  Users,
  MessageSquare,
  BarChart3,
  Filter,
  Star,
  Heart,
  TrendingUp
} from "lucide-react";

export function MarketingPage() {
  const features = [
    {
      icon: Mail,
      title: "Email Campaigns",
      description: "Create and send beautiful email campaigns to your customers. Segment audiences and track performance."
    },
    {
      icon: Palette,
      title: "Canva Design Integration",
      description: "Import professional email designs directly from Canva. No coding or design skills required."
    },
    {
      icon: Zap,
      title: "Marketing Automation",
      description: "Set up automated email sequences triggered by customer actions. Save time while staying engaged."
    },
    {
      icon: Calendar,
      title: "Scheduled Sending",
      description: "Schedule emails to be sent at the perfect time. Plan your campaigns weeks in advance."
    },
    {
      icon: Filter,
      title: "Email Subscriptions",
      description: "Manage subscriber lists with opt-in/opt-out handling. GDPR-compliant subscription management."
    },
    {
      icon: MessageSquare,
      title: "Internal Team Messaging",
      description: "Keep your team connected with real-time messaging. Unified inbox for all business communications."
    },
    {
      icon: BarChart3,
      title: "Campaign Analytics",
      description: "Track opens, clicks, and conversions. Measure ROI and optimize your email strategy."
    },
    {
      icon: Users,
      title: "Audience Segmentation",
      description: "Target specific customer groups with personalized messages. Increase engagement with relevant content."
    }
  ];

  const howItWorksSteps = [
    {
      number: 1,
      title: "Design Your Email",
      description: "Create campaigns using our builder or import designs from Canva",
      icon: Palette
    },
    {
      number: 2,
      title: "Set Up Automation",
      description: "Configure triggers, schedules, and audience segments for your campaign",
      icon: Zap
    },
    {
      number: 3,
      title: "Send or Schedule",
      description: "Send immediately or schedule for optimal timing",
      icon: Calendar
    },
    {
      number: 4,
      title: "Track Performance",
      description: "Monitor engagement metrics and optimize future campaigns",
      icon: BarChart3
    }
  ];

  const relatedFeatures = [
    {
      title: "Reviews & Feedback",
      description: "Follow up with customers after they leave reviews",
      icon: Star,
      link: "/features/reviews"
    },
    {
      title: "Loyalty & Rewards",
      description: "Promote loyalty programs through email campaigns",
      icon: Heart,
      link: "/features/loyalty"
    },
    {
      title: "Analytics & Insights",
      description: "Measure marketing campaign effectiveness",
      icon: TrendingUp,
      link: "/features/analytics"
    }
  ];

  return (
    <FeaturePageLayout>
      <FeatureHero
        title="Powerful Marketing & Communication Tools"
        subtitle="Engage customers with beautiful email campaigns, Canva-powered designs, and smart automation. Plus internal messaging to keep your team connected."
        primaryCTA={{ text: "Start Your First Campaign", link: "/sign-up" }}
        secondaryCTA={{ text: "Explore Features", link: "#how-it-works" }}
      />

      <FeatureSection
        title="Complete Marketing & Communication Suite"
        subtitle="Everything you need to reach customers and coordinate your team"
      >
        <FeatureGrid columns={4}>
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </FeatureGrid>
      </FeatureSection>

      <FeatureSection
        title="How It Works"
        subtitle="Launch sophisticated email campaigns in minutes"
        gradient
      >
        <HowItWorksSection steps={howItWorksSteps} />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="Professional Designs with Canva Integration"
          description="Create stunning emails without any design experience. Import your Canva designs directly into Tag-Inn, or use our built-in template library. Personalize content for different customer segments and track which designs perform best."
          imagePosition="left"
        />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="Automate Your Marketing"
          description="Set up intelligent email sequences that trigger based on customer behavior. Welcome new customers, follow up after reviews, remind about unused loyalty rewards, or re-engage inactive customers. Automation runs 24/7 so you can focus on growing your business."
        />
      </FeatureSection>

      <FeatureSection
        title="Related Features"
        subtitle="Build a complete customer engagement strategy"
        gradient
      >
        <RelatedFeaturesSection features={relatedFeatures} />
      </FeatureSection>

      <FeatureSection>
        <FeatureCTA
          title="Transform How You Communicate"
          description="Start sending professional campaigns that drive results. Email marketing and team collaboration in one platform."
          primaryCTA={{ text: "Create Your First Campaign", link: "/sign-up" }}
          secondaryCTA={{ text: "See Examples", link: "#contact" }}
        />
      </FeatureSection>
    </FeaturePageLayout>
  );
}
